/* eslint-disable no-unused-vars */
import { useReducer, useRef, useMemo, Dispatch } from 'react';
import differenceBy from 'lodash/differenceBy';

import { IFile } from 'typings/document';
import { replaceByItems } from 'utils/helpers';

type FilesState = { files: IFile[] };
// eslint-disable-next-line no-shadow
enum ActionTypes {
  ADD = 'ADD',
  REMOVE = 'REMOVE',
  UPDATE = 'UPDATE',
  RESET = 'RESET',
}
type Action = { type: ActionTypes; payload: IFile[] };
type CommonActionDispatcher = (arg?: IFile[]) => void;

const idField = 'id';

function reducer(state: FilesState, action: Action): FilesState {
  switch (action.type) {
    case ActionTypes.ADD:
      return { files: state.files.concat(...action.payload) };
    case ActionTypes.REMOVE:
      return { files: differenceBy(state.files, action.payload, idField) };
    case ActionTypes.UPDATE:
      return { files: replaceByItems(state.files, action.payload, idField) };
    case ActionTypes.RESET:
      return { files: action.payload };
    default:
      throw new Error();
  }
}

export type FileActionsBag = {
  addFiles: CommonActionDispatcher;
  addRawFiles: (arg0: File[]) => void;
  removeFiles: CommonActionDispatcher;
  updateFiles: CommonActionDispatcher;
  resetFiles: CommonActionDispatcher;
};
const getAction =
  (type: ActionTypes, dispatch: Dispatch<Action>) =>
  (payload: IFile[] | undefined = []) =>
    dispatch({
      type,
      payload,
    });

export default function useFiles(
  initialFiles: IFile[] = []
): [IFile[], FileActionsBag] {
  const incrementalId = useRef(0);
  const initialPayload = useRef(initialFiles);
  const [state, dispatch] = useReducer(reducer, { files: initialFiles });

  const actionsBag = useMemo(
    () => ({
      addFiles: getAction(ActionTypes.ADD, dispatch),
      addRawFiles: (files: File[]) => {
        const payload = files.map((file) => ({
          content: file,
          // eslint-disable-next-line no-plusplus
          id: ++incrementalId.current,
          progress: undefined,
        }));
        dispatch({ type: ActionTypes.ADD, payload });
      },
      removeFiles: getAction(ActionTypes.REMOVE, dispatch),
      updateFiles: getAction(ActionTypes.UPDATE, dispatch),
      resetFiles: (payload = initialPayload.current) =>
        getAction(ActionTypes.RESET, dispatch)(payload),
    }),
    []
  );

  return [state.files, actionsBag];
}
